import React, { useState, useEffect, useCallback } from 'react';
import { Accordion, FormItem, Input } from '@rtkwlf/ui';
import { FormGroup, Input as RSInput, Label } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { OnboardingSections } from '../../../constants';
import { OnboardingSection } from '../OnboardingSection';

import {
  DEFAULT_INTERNET_EXPOSURE,
  onboardingSelector,
} from '../../../reducers/onboarding';
import { ONBOARDING_ACTIONS } from '../../../actions/onboarding';
import { apiRequest } from '../../../APIClient';
import { useOBAccordionChange, withTrashableContext } from '../../../hooks';
import { OnboardingAccordionHeader } from '../OnboardingAccordionHeader';
import { getAccordionHeader, getPluralOrSingularNoun } from '../../../utils';
import {
  featureFlagSelector,
  sessionSelector,
} from '../../../reducers/session';
import ipUtils, {
  IPV4_RANGE_RESERVED_ERROR_MESSAGE,
  PRIVATE_IP_SUBNETS,
  RESERVED_TOP_LEVEL_DOMAINS,
  STATIC_IP_RESERVED_SUBNETS,
} from '../ipUtils';
import { InputValidationObject, InvalidTarget } from '../../../types';
import { useFetchAwnIPs } from '../hooks/useFetchAwnIPs';
import { listIPsSelector } from '../../../reducers/listips';
import Message from '../../../widgets/Message';

export enum ACCORDION_KEYS {
  EXTERNAL_SCAN_HOSTS = 'External Scan Hosts',
  VULNERABILITY_SCAN_SETTINGS = 'Vulnerability Scan Configuration',
}

const ACCORDION_ITEMS: `${ACCORDION_KEYS}`[] = [
  ACCORDION_KEYS.EXTERNAL_SCAN_HOSTS,
  ACCORDION_KEYS.VULNERABILITY_SCAN_SETTINGS,
];

export const RawOnboardingInternetExposure = () => {
  useFetchAwnIPs();
  const dispatch = useDispatch();
  const internetExposure = useSelector(onboardingSelector.getInternetExposure);
  const canEdit = useSelector(sessionSelector.canEditOnboarding);
  const ips = useSelector(listIPsSelector.getIPsList);
  const onlyAddOns = useSelector(onboardingSelector.onlyPurchasedAddOnServices);
  const hasMA = useSelector(onboardingSelector.isManagedAwarenessEnabled);
  const [invalidIps, setInvalidIps] = useState<InvalidTarget[]>([]);
  const [invalidHosts, setInvalidHosts] = useState<InvalidTarget[]>([]);
  const [domainOwnershipConfirmed, setDomainOwnershipConfirmed] =
    useState<boolean>(internetExposure.domainOwnershipConfirmed);
  const [noExternalHosts, setNoExternalHosts] = useState(
    internetExposure.noExternalHosts,
  );
  const dnsOwnershipFF = useSelector(
    featureFlagSelector.onboarding.domainOwnershipConfirmed,
  );
  const onlyMA = React.useMemo(() => onlyAddOns && hasMA, [onlyAddOns, hasMA]);
  const dnsOwnershipComplete = React.useMemo(
    () => !dnsOwnershipFF || internetExposure.domainOwnershipConfirmed,
    [internetExposure, dnsOwnershipFF],
  );
  useEffect(() => {
    if (internetExposure === DEFAULT_INTERNET_EXPOSURE) {
      dispatch(apiRequest.getOBInternetExposure());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    setInvalidIps(
      internetExposure?.invalidTargets?.filter(
        (target) => target.type === 'ip',
      ),
    );
    setInvalidHosts(
      internetExposure?.invalidTargets?.filter(
        (target) => target.type === 'host',
      ),
    );
  }, [internetExposure?.invalidTargets]);

  const { collapseAll, isCollapsed, expandAll, expandedItems, handleOnChange } =
    useOBAccordionChange(ACCORDION_ITEMS);

  const [ipAddresses, setIpAddresses] = useState(internetExposure.ipAddresses);
  const [dnsDomains, setDnsDomains] = useState(internetExposure.dnsDomains);
  const [vulScanIPsConfigured, setVulScanIPsConfigured] = useState(
    internetExposure.vulScanIPsConfigured,
  );

  const [isUpdated, setIsUpdated] = useState(false);

  useEffect(() => {
    if (!isUpdated && internetExposure !== DEFAULT_INTERNET_EXPOSURE) {
      setIpAddresses(internetExposure.ipAddresses);
      setDnsDomains(internetExposure.dnsDomains);
      setDomainOwnershipConfirmed(internetExposure.domainOwnershipConfirmed);
      setVulScanIPsConfigured(internetExposure.vulScanIPsConfigured);
      setNoExternalHosts(internetExposure.noExternalHosts);
      setIsUpdated(true);
    }
  }, [internetExposure, isUpdated]);

  useEffect(() => {});

  const updateAddresses = useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      setIpAddresses(e.currentTarget.value);
    },
    [setIpAddresses],
  );

  const updateDnsDomains = useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      setDnsDomains(e.currentTarget.value);
    },
    [setDnsDomains],
  );

  const updatedomainOwnershipConfirmed = useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      setDomainOwnershipConfirmed(e.currentTarget.checked);
      dispatch(
        ONBOARDING_ACTIONS.EDIT_INTERNET_EXPOSURE({
          ...internetExposure,
          domainOwnershipConfirmed: e.currentTarget.checked,
          completed:
            (!!internetExposure.ipAddresses || onlyMA) &&
            !!internetExposure.dnsDomains &&
            (internetExposure.vulScanIPsConfigured || onlyMA) &&
            (!dnsOwnershipFF || e.currentTarget.checked),
        }),
      );
      dispatch(apiRequest.saveOBInternetExposure());
    },
    [
      setDomainOwnershipConfirmed,
      dispatch,
      internetExposure,
      onlyMA,
      dnsOwnershipFF,
    ],
  );

  const updateNoExternalHosts = useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      setNoExternalHosts(e.currentTarget.checked);
      if (e.currentTarget.checked) {
        setDnsDomains('');
        setIpAddresses('');
        dispatch(
          ONBOARDING_ACTIONS.EDIT_INTERNET_EXPOSURE({
            ...internetExposure,
            ipAddresses: '',
            dnsDomains: '',
            domainOwnershipConfirmed: false,
            completed: true,
            noExternalHosts: e.currentTarget.checked,
          }),
        );
      } else {
        dispatch(
          ONBOARDING_ACTIONS.EDIT_INTERNET_EXPOSURE({
            ...internetExposure,
            ipAddresses: ipAddresses,
            dnsDomains: dnsDomains,
            domainOwnershipConfirmed: domainOwnershipConfirmed,
            completed:
              (!!internetExposure.ipAddresses || onlyMA) &&
              !!internetExposure.dnsDomains &&
              (internetExposure.vulScanIPsConfigured || onlyMA) &&
              dnsOwnershipComplete,
            noExternalHosts: e.currentTarget.checked,
          }),
        );
      }
      dispatch(apiRequest.saveOBInternetExposure());
    },
    [
      ipAddresses,
      dnsDomains,
      domainOwnershipConfirmed,
      setNoExternalHosts,
      dispatch,
      internetExposure,
      onlyMA,
      dnsOwnershipComplete,
    ],
  );

  const externalScanHostsCompleted =
    internetExposure.noExternalHosts ||
    onlyMA ||
    ((!!internetExposure.ipAddresses || onlyMA) &&
      !!internetExposure.dnsDomains &&
      dnsOwnershipComplete);

  const externalScanTitle = getAccordionHeader(
    externalScanHostsCompleted,
    expandedItems,
    ACCORDION_KEYS.EXTERNAL_SCAN_HOSTS,
    noExternalHosts
      ? 'No external scan hosts'
      : getPluralOrSingularNoun(
          ipAddresses.split(',').length + dnsDomains.split(',').length,
          'external scan host',
        ),
    'External scan hosts incomplete',
  );

  const vulScanTitle = () => {
    if (internetExposure.noExternalHosts) {
      return vulScanIPsConfigured
        ? 'Vulnerability scan configuration complete'
        : 'No vulnerability scan configuration';
    } else {
      return vulScanIPsConfigured
        ? 'Vulnerability scan configuration complete'
        : 'Vulnerability scan configuration incomplete';
    }
  };

  const ipAddressBlur = useCallback(() => {
    dispatch(
      ONBOARDING_ACTIONS.EDIT_INTERNET_EXPOSURE({
        ...internetExposure,
        ipAddresses: ipAddresses,
        completed:
          !!ipAddresses &&
          !!internetExposure.dnsDomains &&
          internetExposure.vulScanIPsConfigured &&
          dnsOwnershipComplete,
      }),
    );
    dispatch(apiRequest.saveOBInternetExposure());
  }, [dispatch, internetExposure, ipAddresses, dnsOwnershipComplete]);

  const dnsDomainsBlur = useCallback(() => {
    let isDNSChanged = internetExposure.dnsDomains !== dnsDomains;
    let newDnsConfirmed =
      dnsOwnershipFF && isDNSChanged
        ? false
        : internetExposure.domainOwnershipConfirmed;
    setDomainOwnershipConfirmed(newDnsConfirmed);
    dispatch(
      ONBOARDING_ACTIONS.EDIT_INTERNET_EXPOSURE({
        ...internetExposure,
        dnsDomains: dnsDomains,
        completed:
          (!!internetExposure.ipAddresses || onlyMA) &&
          !!dnsDomains &&
          (internetExposure.vulScanIPsConfigured || onlyMA) &&
          (!dnsOwnershipFF || newDnsConfirmed),
        domainOwnershipConfirmed: newDnsConfirmed,
      }),
    );
    dispatch(apiRequest.saveOBInternetExposure());
  }, [dispatch, dnsDomains, internetExposure, onlyMA, dnsOwnershipFF]);

  const vulScanConfiguredChange = useCallback(
    (e) => {
      setVulScanIPsConfigured(e.currentTarget.checked);
      dispatch(
        ONBOARDING_ACTIONS.EDIT_INTERNET_EXPOSURE({
          ...internetExposure,
          vulScanIPsConfigured: e.currentTarget.checked,
          completed:
            (!!internetExposure.ipAddresses &&
              !!internetExposure.dnsDomains &&
              e.currentTarget.checked &&
              dnsOwnershipComplete) ||
            internetExposure.noExternalHosts,
        }),
      );
      dispatch(apiRequest.saveOBInternetExposure());
    },
    [dispatch, internetExposure, dnsOwnershipComplete],
  );

  const ipAddressesError: InputValidationObject = React.useMemo(() => {
    const splitIPAddresses = ipUtils.splitIPv4Addresses(ipAddresses);
    if (!ipAddresses || noExternalHosts || !canEdit) {
      return {};
    }

    if (splitIPAddresses.error) {
      return {
        status: 'error',
        message: splitIPAddresses.error,
      };
    }

    for (const ip of splitIPAddresses.ipAddresses) {
      // Check if each IP is a valid non-reserved address
      if (
        ipUtils.isIPv4InSubnets(ip, [
          ...STATIC_IP_RESERVED_SUBNETS,
          ...PRIVATE_IP_SUBNETS,
        ])
      ) {
        return {
          status: 'error',
          message: IPV4_RANGE_RESERVED_ERROR_MESSAGE,
        };
      }
    }
    return { status: 'success' };
  }, [ipAddresses, canEdit, noExternalHosts]);

  const dnsDomainsError: InputValidationObject = React.useMemo(() => {
    if (!dnsDomains || noExternalHosts || !canEdit) {
      return {};
    }
    for (const domain of RESERVED_TOP_LEVEL_DOMAINS) {
      if (dnsDomains.includes(domain)) {
        return {
          status: 'error',
          message: 'Remove reserved top-level domain (TLD) names',
        };
      }
    }
    return { status: 'success' };
  }, [dnsDomains, canEdit, noExternalHosts]);

  return (
    <OnboardingSection
      title={OnboardingSections.INTERNET_EXPOSURE}
      completed={internetExposure.completed}
    >
      <OnboardingAccordionHeader
        description="This information is used to perform vulnerability scans of your public internet presence."
        isCollapsed={isCollapsed}
        expandAll={expandAll}
        collapseAll={collapseAll}
      />
      <div className="ob-accordion-section">
        <Accordion
          itemGap={20}
          expandedItems={expandedItems}
          onChange={handleOnChange}
        >
          <Accordion.Item
            className="mr-3"
            itemKey={ACCORDION_KEYS.EXTERNAL_SCAN_HOSTS}
            header={externalScanTitle}
            data-cy="external-accordion"
            footer={
              <div className="pl-1 pt-2">
                <Label className="ob-accordion-checkbox" check>
                  <RSInput
                    type="checkbox"
                    id="noExternalHosts"
                    checked={noExternalHosts}
                    onChange={updateNoExternalHosts}
                    disabled={!canEdit}
                  />
                  I don't have any external hosts to scan
                </Label>
              </div>
            }
          >
            <p className="ob-section-description">
              Provide all public {!onlyMA && 'IP addresses and'} domains owned
              by your organization to be scanned.
            </p>
            <FormGroup>
              {!onlyMA && (
                <div className="pl-1 pt-2">
                  <h5 className="pb-1">Public IP Addresses/Ranges</h5>
                  <span style={{ fontSize: '11px' }}>
                    Max IP ranges supported are /21 Example: 198.52.100.4,
                    198.52.100.13-198.52.100.23, 203.0.113.0/24
                  </span>
                  <FormItem
                    {...ipAddressesError}
                    hasIcon={false}
                    data-cy="ipAddresses"
                  >
                    <Input
                      disabled={noExternalHosts || !canEdit}
                      htmlType="textarea"
                      id="ipAddresses"
                      value={ipAddresses}
                      onChange={updateAddresses}
                      onBlur={ipAddressBlur}
                    />
                  </FormItem>
                  {!!invalidIps?.length && (
                    <Message
                      title="Error"
                      message="The following IP addresses do not resolve:"
                      type="warning"
                    >
                      <ul>
                        {invalidIps.map((target) => (
                          <li key={target.target}>
                            {target.target && <b>{target.target}:</b>}{' '}
                            {target.reason}
                          </li>
                        ))}
                      </ul>
                    </Message>
                  )}
                </div>
              )}
              <div className="pl-1 pt-2">
                <h5 className="pb-1">Public DNS Domains</h5>
                <span style={{ fontSize: '11px' }}>
                  Example: arcticwolf.com
                </span>
                <FormItem
                  {...dnsDomainsError}
                  hasIcon={false}
                  data-cy="dnsDomains"
                >
                  <Input
                    disabled={noExternalHosts || !canEdit}
                    htmlType="textarea"
                    id="dnsDomains"
                    value={dnsDomains}
                    onChange={updateDnsDomains}
                    onBlur={dnsDomainsBlur}
                  />
                </FormItem>
                {!!invalidHosts?.length && (
                  <Message
                    title="Error"
                    message="The following domains do not resolve:"
                    type="warning"
                  >
                    <ul>
                      {invalidHosts.map((target) => (
                        <li key={target.target}>
                          {target.target && <b>{target.target}:</b>}{' '}
                          {target.reason}
                        </li>
                      ))}
                    </ul>
                  </Message>
                )}
              </div>
              {dnsOwnershipFF && (
                <div className="pl-1 pt-2">
                  <Label className="ob-accordion-checkbox" check>
                    <RSInput
                      type="checkbox"
                      id="domainOwnershipConfirmed"
                      checked={domainOwnershipConfirmed}
                      onChange={updatedomainOwnershipConfirmed}
                      disabled={!canEdit}
                    />
                    By checking this box we reaffirm our organization owns the
                    designated domain(s) and authorize Arctic Wolf to perform
                    vulnerability scanning including account takeover scanning
                    on such domain(s) and any subsequent subdomain(s) we may
                    provide to Arctic Wolf.
                  </Label>
                </div>
              )}
            </FormGroup>
          </Accordion.Item>
          {!onlyMA && (
            <Accordion.Item
              className="mr-3"
              itemKey={ACCORDION_KEYS.VULNERABILITY_SCAN_SETTINGS}
              header={vulScanTitle()}
            >
              <p className="ob-section-description">
                Arctic Wolf performs monthly EVA scans against customer external
                IP ranges. These IP ranges show as the source:
              </p>
              {ips?.externalScans?.length > 0 && (
                <>
                  <p className="ob-section-description">IP Address Range</p>
                  <ul className="ob-section-description ml-4">
                    {ips.externalScans.map((ip) => (
                      <li key={ip}>{ip}</li>
                    ))}
                  </ul>
                </>
              )}
              <p className="ob-section-description">
                Note: You have the option to add the range to your firewall if
                the firewall aggressively drops our scan attempts, but we
                recommend demonstrating a real world scan that bad actors could
                use to do recon. Contact your Concierge Security Team for more
                information.
              </p>
              <FormGroup>
                <div className="pl-1 pt-2">
                  <Label className="ob-accordion-checkbox" check>
                    <RSInput
                      type="checkbox"
                      id="vulScanIPsConfigured"
                      checked={vulScanIPsConfigured}
                      disabled={!canEdit}
                      onChange={vulScanConfiguredChange}
                    />
                    Acknowledged
                  </Label>
                </div>
              </FormGroup>
            </Accordion.Item>
          )}
        </Accordion>
      </div>
    </OnboardingSection>
  );
};

const OnboardingInternetExposure = withTrashableContext(
  RawOnboardingInternetExposure,
);

export default OnboardingInternetExposure;
